import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    DisplayEventProfile,
    inject,
    Modal,
    ModalHandle,
    TasksStore,
    objectToHumanString,
    humanizeString,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { AccountsStore } from '../Accounts/AccountsStore';
import { AlertActionsStore } from '../DisplayAlerts/AlertActionsStore';
import { DisplayEventProfileActions, DisplayEventProfileViews } from './DisplayEventProfilesList';
import { DisplayEventProfileStore } from './DisplayEventProfileStore';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';
import { BsSubtract } from '@react-icons/all-files/bs/BsSubtract';

export interface IDisplayEventProfileItemProps {
    readonly displayEventProfile: Mri.DisplayEventProfiles.DisplayEventProfile;
}

@autoinject
@observer
export class DisplayEventProfileItem extends React.Component<IDisplayEventProfileItemProps> {
    public render() {
        const location = `/display-event-profiles`;
        const eventProfile = this.props.displayEventProfile;
        const viewType =
            eventProfile.displayEventStatus != null
                ? DisplayEventProfileViews.Automations
                : DisplayEventProfileViews.Alerts;

        return (
            <tr>
                <td>{this.accountName}</td>

                <td className="fw-bold">{eventProfile.name} </td>

                {this.props.displayEventProfile.alertActionGuid && (
                    <td>
                        <div className="mw-600 mh-200 overflow-auto no-wrap">
                            {this.alertAction?.actionRecipients.map((r, idx) => (
                                <div key={idx}>
                                    {r.type == Mri.DisplayAlerts.RecipientType.Email ||
                                    r.type == Mri.DisplayAlerts.RecipientType.WarrantyEmail
                                        ? this.alertActionsStore.recipients.get(r.value)?.email
                                        : r.type == Mri.DisplayAlerts.RecipientType.SMS
                                            ? this.alertActionsStore.recipients.get(r.value)?.phoneNumber
                                            : r.type == Mri.DisplayAlerts.RecipientType.AccountSubscribers
                                                ? 'All Account Subscribers'
                                                : r.value}
                                </div>
                            ))}
                        </div>
                    </td>
                )}

                {this.props.displayEventProfile.alertActionGuid == null &&
                    this.props.displayEventProfile.displayEventStatus == null && (
                    <td>
                        <div className="fw-bold text-danger">Error: Define Alert Action or Status!</div>
                    </td>
                )}

                <td>{eventProfile.comment}</td>

                <td>
                    <div className="mw-600 mh-200 overflow-auto">
                        {DisplayEventProfile.readableForm(eventProfile, this.alertAction, !this.showingAllEvents)}
                        {DisplayEventProfile.arrayOfEvents(eventProfile).length > 3 && (
                            <button className="btn btn-link px-0 d-block" onClick={this.toggleAllEvents}>
                                {!this.showingAllEvents ? 'Show More' : 'Collapse'}
                            </button>
                        )}
                    </div>
                </td>

                <td>
                    <div className="mw-600 mh-200 overflow-auto">
                        <pre>
                            <code>{objectToHumanString(eventProfile.displayFilter, true)}</code>
                        </pre>
                    </div>
                </td>

                <td>
                    <div className="d-flex">
                        <LinkContainer
                            exact
                            to={
                                location +
                                `/${viewType}/${eventProfile.displayEventProfileGuid}/${DisplayEventProfileActions.Edit}`
                            }
                            className="btn btn-link fw-bold py-0 ms-auto"
                        >
                            <a onClick={this.onToggle}>
                                <BsPencilSquare />
                            </a>
                        </LinkContainer>

                        <LinkContainer
                            exact
                            to={
                                location +
                                `/${viewType}/${eventProfile.displayEventProfileGuid}/${DisplayEventProfileActions.Clone}`
                            }
                            className="btn btn-link fw-bold py-0 px-1"
                        >
                            <a onClick={this.onToggle}>
                                <BsSubtract />
                            </a>
                        </LinkContainer>

                        <a className="btn text-danger py-0" onClick={this.deleteAsync}>
                            <BsTrash className="" />
                        </a>
                    </div>
                </td>
                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">{'Delete ' + this.props.displayEventProfile.name + '?'}</div>
                </Modal>
            </tr>
        );
    }

    @bind
    @action
    private async toggleAllEvents(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.showingAllEvents = !this.showingAllEvents;
    }

    @bind
    @action
    private async deleteAsync(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        if (await this.modalHandle.confirm()) {
            const task = this.tasksStore.startSessionTask('Delete Display Event Profile');

            try {
                this.displayEventProfileStore.deleteAsync(this.props.displayEventProfile.displayEventProfileGuid);
                task.complete();
            } catch (e: any) {
                task.error(e);
            }
        }
    }

    @computed
    private get alertAction() {
        if (this.props.displayEventProfile && this.props.displayEventProfile.alertActionGuid) {
            return this.alertActionsStore.alertActions.get(this.props.displayEventProfile.alertActionGuid);
        }
        return;
    }

    @computed
    private get accountName() {
        if (this.alertAction) {
            const account = this.accountsStore.accounts.get(this.alertAction.account);
            return account ? account.accountName : '-';
        }
        return '-';
    }

    @bind
    @action
    private onToggle() {
        this.collapsed = !this.collapsed;
    }

    @observable
    private collapsed = true;
    @observable
    private showingAllEvents = false;
    @observable
    private modalHandle = new ModalHandle();
    @inject
    private accountsStore!: AccountsStore;
    @inject
    private alertActionsStore!: AlertActionsStore;
    @inject
    private displayEventProfileStore!: DisplayEventProfileStore;
    @inject
    private tasksStore!: TasksStore;
}
